/** Filters and recommendations around Horizon's existing slideshow component. */
class LKNativeCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.prepareImages();
    this.addEventListener('click', this.onFilter, { signal: this.controller.signal });
    this.scroller = this.querySelector('.lk-native__slideshow > slideshow-container > slideshow-slides');
    this.progress = this.querySelector('[data-carousel-progress]');
    if (this.scroller) {
      this.scroller.addEventListener('scrollend', this.syncArrows, { signal: this.controller.signal });
      this.resizeObserver = new ResizeObserver(this.syncArrows);
      this.resizeObserver.observe(this.scroller);
      customElements.whenDefined('slideshow-component').then(() => this.setupLoop());
    }
    if (this.hasAttribute('data-initial-filter')) {
      customElements.whenDefined('slideshow-component').then(() => {
        if (!this.isConnected || !this.hasAttribute('data-initial-filter')) return;
        this.initialFrame = requestAnimationFrame(() => {
          const button = this.querySelector(`button[data-filter="${this.dataset.initialFilter}"]`);
          if (button) this.onFilter({ target: button });
        });
      });
    }
    if (this.dataset.recommendations || this.dataset.sectionId) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.loadProducts();
        }
      }, {
        root: this.dataset.sectionId ? this.closest('.page-wrapper') : null,
        rootMargin: this.dataset.sectionId ? '1400px' : '300px',
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.loading = false;
    this.observer?.disconnect();
    this.imageObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.initialFrame);
  }

  prepareImages() {
    const images = [...this.querySelectorAll('img[data-lk-src]')];
    const load = (image) => {
      image.loading = 'eager';
      if (image.dataset.lkSrcset) image.srcset = image.dataset.lkSrcset;
      image.src = image.dataset.lkSrc;
      delete image.dataset.lkSrcset;
      delete image.dataset.lkSrc;
    };
    if (!images.length) return;
    if (!('IntersectionObserver' in window)) {
      images.forEach(load);
      return;
    }
    const wrapper = this.closest('.page-wrapper');
    const root = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : null;
    this.imageObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!this.isConnected || !entry.isIntersecting || !entry.target.dataset.lkSrc) continue;
        load(entry.target);
        this.imageObserver.unobserve(entry.target);
      }
    }, { root, rootMargin: '160px 0px' });
    images.forEach((image) => this.imageObserver.observe(image));
  }

  /**
   * Horizon's infinite mode steps by "current index ± visible slides" and wraps any
   * negative index to the last slide, so with several cards per view the first card
   * becomes unreachable from the arrows. Looping slideshows page by scroll position instead.
   */
  setupLoop() {
    const slideshow = this.querySelector('slideshow-component');
    if (!this.isConnected || !slideshow?.hasAttribute('infinite')) return;
    this.loop = true;
    // Instance methods shadow the prototype, so the arrows (on:click="/next") and autoplay both land here.
    slideshow.next = (event) => this.page(1, event);
    slideshow.previous = (event) => this.page(-1, event);
    this.syncArrows();
  }

  page(direction, event) {
    event?.preventDefault?.();
    const scroller = this.scroller;
    if (!scroller) return;
    const end = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    if (end <= 1) return;
    // One page = the visible width plus the gap after it, which lands exactly on the next card's start.
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const step = scroller.clientWidth + gap;
    const position = scroller.scrollLeft;
    let target;
    if (direction > 0) target = position >= end - 1 ? 0 : Math.min(end, position + step);
    else target = position <= 1 ? end : Math.max(0, position - step);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({ left: target, behavior: reduce ? 'instant' : 'smooth' });
  }

  syncArrows = () => {
    const scroller = this.scroller;
    if (!scroller) return;
    const arrows = scroller.parentElement.querySelector(':scope > slideshow-arrows');
    if (!arrows) return;
    const end = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    arrows.hidden = end <= 1;
    if (this.progress) {
      const filled = end > 1 ? (scroller.scrollLeft + scroller.clientWidth) / scroller.scrollWidth : 1;
      this.progress.style.setProperty('--lk-carousel-progress', String(Math.min(1, Math.max(0, filled))));
    }
    const previous = arrows.querySelector('[ref="previous"]');
    const next = arrows.querySelector('[ref="next"]');
    // A looping carousel wraps at both ends, so neither arrow is ever disabled.
    if (previous) previous.disabled = !this.loop && scroller.scrollLeft <= 1;
    if (next) next.disabled = !this.loop && scroller.scrollLeft >= end - 1;
  };

  onFilter = (event) => {
    const button = event.target.closest('button[data-filter]');
    if (!button || !this.contains(button)) return;
    const filter = button.dataset.filter;
    if (this.dataset.sectionId) {
      this.pendingFilter = filter;
      this.loadProducts();
      return;
    }
    this.removeAttribute('data-initial-filter');
    cancelAnimationFrame(this.initialFrame);
    const slideshow = this.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector(':scope > slideshow-container > slideshow-slides');
    if (!scroller) return;
    const limit = Number(this.dataset.limit) || Infinity;
    let visible = 0;
    for (const slide of scroller.children) {
      if (slide.hasAttribute('data-promo')) continue;
      const matches = filter === 'all' || (slide.dataset.audiences || '').split('|').includes(filter);
      slide.hidden = !matches || visible >= limit;
      slide.removeAttribute('reveal');
      if (!slide.hidden) visible++;
      slide.setAttribute('aria-hidden', String(slide.hidden));
    }
    this.querySelectorAll('button[data-filter]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === button));
    });
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = visible > 0;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      // Reset the native carousel after its visible slide list changes.
      scroller.scrollTo({ left: 0, behavior: 'instant' });
      slideshow.current = 0;
      slideshow.select?.(0, undefined, { animate: false });
      this.syncArrows();
    });
  };

  async loadProducts() {
    if (this.loading) return;
    this.loading = true;
    const signal = this.controller.signal;
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(this.dataset.recommendations || window.location.href, window.location.origin);
      if (this.dataset.sectionId) {
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
      }
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Products unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = doc.getElementById(this.id);
      if (!replacement) {
        // An empty Sale section is intentionally omitted by Liquid.
        const section = this.dataset.sectionId && doc.getElementById(`shopify-section-${this.dataset.sectionId}`);
        if (section && !section.textContent.trim()) {
          if (this.isConnected && !signal.aborted) this.remove();
          return;
        }
        throw new Error('No products returned');
      }
      if (replacement.dataset.recommendations || replacement.dataset.sectionId) throw new Error('Incomplete products');
      if (this.pendingFilter) replacement.dataset.initialFilter = this.pendingFilter;
      if (this.isConnected && !signal.aborted) this.replaceWith(replacement);
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
      const status = this.querySelector('[data-load-status]') || this.querySelector('[role="status"]');
      if (status) {
        status.hidden = false;
        status.textContent = 'Explore the collection for more products.';
      }
    } finally {
      if (this.controller?.signal === signal) this.loading = false;
    }
  }
}

if (!customElements.get('lk-native-carousel')) {
  customElements.define('lk-native-carousel', LKNativeCarousel);
}
